import React, { useState, useRef, useEffect } from 'react';
import { Eye } from 'lucide-react';

interface LinkTooltipProps {
  url: string;
  title?: string;
  children: React.ReactNode;
  className?: string;
  onOpenModal?: () => void;
}

const LinkTooltip: React.FC<LinkTooltipProps> = ({ 
  url, 
  title, 
  children, 
  className = '',
  onOpenModal
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isTouchDevice, setIsTouchDevice] = useState(false);

  // Detect if this is a touch device
  useEffect(() => {
    const detectTouch = () => {
      setIsTouchDevice('ontouchstart' in window || navigator.maxTouchPoints > 0);
    };
    detectTouch();
  }, []);

  const showTooltip = () => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    setIsVisible(true);
    
    // Use setTimeout to ensure DOM is updated before calculating position
    setTimeout(() => {
      if (triggerRef.current && tooltipRef.current) {
        const triggerRect = triggerRef.current.getBoundingClientRect();
        const tooltipRect = tooltipRef.current.getBoundingClientRect();
        
        // Position tooltip above the trigger element by default
        let top = triggerRect.top - tooltipRect.height - 12; // Increased gap
        let left = triggerRect.left + (triggerRect.width / 2) - (tooltipRect.width / 2);
        
        // Adjust if tooltip goes off screen vertically
        if (top < 10) {
          top = triggerRect.bottom + 12; // Show below if no space above, increased gap
        }
        
        // Adjust if tooltip goes off screen horizontally
        if (left < 10) {
          left = 10;
        } else if (left + tooltipRect.width > window.innerWidth - 10) {
          left = window.innerWidth - tooltipRect.width - 10;
        }
        
        setPosition({ top, left });
      }
    }, 0);
  };

  const handleMouseEnter = () => {
    // Add a small delay before showing tooltip for hover devices
    timeoutRef.current = setTimeout(() => {
      showTooltip();
    }, 200); // Reduced delay for better responsiveness
  };

  const handleMouseLeave = () => {
    // Clear any pending timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    
    // Add a small delay before hiding to allow moving to tooltip
    timeoutRef.current = setTimeout(() => {
      setIsVisible(false);
    }, 100);
  };

  const handleClick = (e: React.MouseEvent) => {
    // Always prevent default click behavior and show tooltip first
    e.preventDefault();
    e.stopPropagation();
    
    // If tooltip is not visible, show it
    if (!isVisible) {
      showTooltip();
    }
    // If tooltip is already visible, keep it visible (don't navigate)
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    // Show tooltip immediately on touch start
    e.preventDefault();
    showTooltip();
  };

  // Handle clicking outside to close tooltip on touch devices
  useEffect(() => {
    if (!isTouchDevice || !isVisible) return;

    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (
        triggerRef.current && 
        tooltipRef.current && 
        !triggerRef.current.contains(event.target as Node) &&
        !tooltipRef.current.contains(event.target as Node)
      ) {
        setIsVisible(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    
    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isTouchDevice, isVisible]);

  const handleTooltipMouseEnter = () => {
    // Clear any pending hide timeout when entering tooltip
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsVisible(true);
  };

  const handleTooltipMouseLeave = () => {
    // For touch devices, don't hide on mouse leave since touch doesn't have hover
    if (isTouchDevice) return;
    
    // Hide immediately when leaving tooltip on non-touch devices
    setIsVisible(false);
  };

  const handleOpenInModal = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onOpenModal) {
      onOpenModal();
    }
    setIsVisible(false);
  };

  // Get domain for display
  const getDomain = () => {
    try {
      return new URL(url).hostname;
    } catch {
      return url;
    }
  };

  return (
    <>
      <div
        ref={triggerRef}
        className={`relative ${className}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
        onClickCapture={handleClick} // Capture phase to prevent child clicks
        onTouchStart={handleTouchStart}
      >
        {children}
      </div>
      
      {/* Tooltip Portal */}
      {isVisible && (
        <div
          ref={tooltipRef}
          className="fixed z-[60] bg-white border border-gray-200 rounded-lg shadow-xl p-3 min-w-[220px] max-w-[320px] transition-opacity duration-200"
          style={{
            top: position.top,
            left: position.left,
          }}
          onMouseEnter={handleTooltipMouseEnter}
          onMouseLeave={handleTooltipMouseLeave}
        >
          <div className="text-sm">
            <div className="font-medium text-gray-900 mb-1 truncate">
              {title || 'External Link'}
            </div>
            <div className="text-gray-500 text-xs mb-3 truncate">
              🌐 {getDomain()}
            </div>
            <div className="space-y-2">
              {onOpenModal && (
                <button
                  onClick={handleOpenInModal}
                  className="flex items-center space-x-2 w-full px-2 py-1.5 text-gray-600 hover:text-gray-800 hover:bg-gray-50 text-sm font-medium transition-colors rounded border border-gray-200 hover:border-gray-300"
                >
                  <Eye className="w-4 h-4" />
                  <span>Open</span>
                </button>
              )}
            </div>
          </div>
          
          {/* Tooltip Arrow */}
          <div 
            className="absolute w-3 h-3 bg-white border border-gray-200 transform rotate-45"
            style={{
              bottom: position.top < window.innerHeight / 2 ? '-6px' : 'auto',
              top: position.top >= window.innerHeight / 2 ? '-6px' : 'auto',
              left: '50%',
              transform: 'translateX(-50%) rotate(45deg)',
              borderTop: position.top >= window.innerHeight / 2 ? '1px solid #e5e7eb' : 'none',
              borderLeft: position.top >= window.innerHeight / 2 ? '1px solid #e5e7eb' : 'none',
              borderBottom: position.top < window.innerHeight / 2 ? '1px solid #e5e7eb' : 'none',
              borderRight: position.top < window.innerHeight / 2 ? '1px solid #e5e7eb' : 'none',
            }}
          />
        </div>
      )}
    </>
  );
};

export default LinkTooltip;
